// Lion Auto no-comp step-down (criteria.noCompDropPct), as the page words it.
// With no anchor comp (no market listing in the group's sections survives its
// filters, nor in their zone with the zone fallback on) and a rate above 0,
// the Auto runner lowers each ranked listing's B by that % a day (a step
// every NO_COMP_STEP_HOURS, at least $1 a step), never under its floor, until
// a comp appears or it sells; 0 = the price holds (as before the key). The
// server keeps a clock per group (GroupView autoNoComp { since, dropAt,
// nextAt }, UTC). Calculate previews the runner's answer (the ladder
// answer's noComp { pct, since, dropAt, nextAt, steps }, each stepped rung
// basis 'stepDown' with no what-if; status STEP_DOWN only when a ranked
// listing could step: each needs a B and a cost for its floor) and never
// writes it (lionAutoLadder ladderEndAction 'stepDown'). These helpers clean
// the rate as the server bounds it and word it for the editor, the ladder
// bar, the table and the group card. Pure; tested in lionAutoNoComp.test.js.
import { cleanLadderAmount, pctText } from './lionAutoLadder';
import { dueText, easternText } from './lionAutoRunner';

// The server's bound (compMatchRules LIMITS.noCompDropPct): % of B a day,
// cents allowed
export const MAX_NO_COMP_DROP_PCT = 50;
// What the New group form starts with (as a Lion Checker import's creates:
// importRules IMPORT_DEFAULTS)
export const NEW_GROUP_NO_COMP_DROP_PCT = 5;
// Wording only: mirrors noCompRules.NO_COMP_STEP_MS (6 h; 4 steps a day)
export const NO_COMP_STEP_HOURS = 6;

// A stepped rung's New L cell: no what-if was asked for it
export const STEP_DOWN_L_TITLE = 'No what-if for a step down: Skybox gives its L once the price is written';

const STEP_BASIS = 'stepDown';
// Why a compless group with the rate on had nothing to step
const NO_STEPPABLE = 'each needs a B and a cost in Skybox for its floor';
// The server held the step-down: the payload had no priced market row at all
// (noCompRules.marketSeen false; the ladder answer's and a run summary's noCompHold)
const NO_MARKET_HOLD = 'NO_MARKET';
const HELD_BANNER_TEXT = 'No priced market listing at this event in Skybox: the step-down is held until one appears '
  + '(a broken market feed looks the same). Calculate writes nothing.';
const HELD_CHIP_TITLE = 'Held: Skybox showed no priced listing at this event, so the step-down waits (a broken market feed looks the same)';
const isObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const usd = (v) => `$${Math.abs(v).toFixed(2)}`;
const finite = (v) => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** The rate: 0..50 % a day rounded to the cent; blank or junk = 0 (off) */
export function cleanNoCompPct(v) {
  return cleanLadderAmount(v, 0, MAX_NO_COMP_DROP_PCT);
}

/** The editor's hint for a rate */
export function noCompHintText(pct) {
  const p = cleanNoCompPct(pct);
  if (p <= 0) return '0 = hold the price while there is no comp.';
  return `When no market listing matches, Auto lowers each ranked listing's B ${pctText(p)} a day `
    + `(a step every ${NO_COMP_STEP_HOURS} h, at least $1 a step), never under its floor. Calculate never writes these.`;
}

/** The ladder bar's head line: "no comp: −5% a day to the floor"; null when off */
export function noCompHeadText(pct) {
  const p = cleanNoCompPct(pct);
  return p > 0 ? `no comp: −${pctText(p)} a day to the floor` : null;
}

// "next step in 3h, Oct 21, 2026, 2:00 PM ET" / "a step is due now: Auto
// takes it on its next run" (Calculate never writes it); then "; since …"
// when readable. The answer's clock first, else the group's.
function clockText(noComp, clock, now) {
  const nextAt = noComp.nextAt ?? clock.nextAt;
  const since = easternText(noComp.since ?? clock.since);
  const next = easternText(nextAt);
  const parts = [];
  if (finite(noComp.steps) > 0) parts.push('a step is due now: Auto takes it on its next run');
  else if (next) parts.push(`next step ${dueText(nextAt, now)}, ${next}`);
  if (since) parts.push(`since ${since}`);
  return parts.length ? ` (${parts.join('; ')})` : '';
}

/**
 * The ladder bar's banner for a compless answer with the step-down on (it
 * replaces the plain "No anchor comp" banner): shown when the status has
 * STEP_DOWN, or NO_ANCHOR_COMP, and the answer's noComp.pct is above 0. A
 * held answer (noCompHold 'NO_MARKET': no priced market listing in the
 * payload, so no noComp) gets a warning that the step-down waits for one.
 * NO_ANCHOR_COMP without STEP_DOWN: no ranked listing could step (each needs
 * a B and a cost for its floor), a warning. Else, Auto on with a clock: the
 * rate, the next step (or "a step is due now" when the answer stepped) and
 * since when; Auto on with no clock yet: it starts on its next run; Auto off
 * (or a group not saved yet): what Auto would do. Always "Calculate writes
 * nothing"; the runner in dry run says so.
 * @param {object|null} data  the ladder answer (status, noComp, noCompHold)
 * @param {{ group?: object|null, dryRun?: boolean|null, zoneFallback?: boolean, now?: number }} [ctx]
 *   group: the saved group (GroupView: autoEnabled, autoNoComp), null for a draft; dryRun: the
 *   runner's mode (null = unknown); zoneFallback: criteria.compZoneFallback; now: epoch ms
 * @returns {{ code: 'STEP_DOWN', kind: ''|'warn', text: string }|null}
 */
export function noCompBanner(data, { group = null, dryRun = null, zoneFallback = false, now = Date.now() } = {}) {
  if (data?.noCompHold === NO_MARKET_HOLD) return { code: 'STEP_DOWN', kind: 'warn', text: HELD_BANNER_TEXT };
  const codes = Array.isArray(data?.status) ? data.status : [];
  const noComp = isObject(data?.noComp) ? data.noComp : null;
  const pct = cleanNoCompPct(noComp?.pct);
  if (!noComp || pct <= 0 || !(codes.includes('STEP_DOWN') || codes.includes('NO_ANCHOR_COMP'))) return null;
  const where = zoneFallback === true ? 'in the sections or the zone' : 'in the sections';
  if (!codes.includes('STEP_DOWN')) {
    return { code: 'STEP_DOWN', kind: 'warn', text: `No comp ${where}, and no ranked listing can step down (${NO_STEPPABLE}), so nothing was written.` };
  }
  const rate = `${pctText(pct)} a day`;
  const clock = isObject(group?.autoNoComp) ? group.autoNoComp : null;
  let what = `with Auto on, the ranked listings would drop ${rate} toward the floor`;
  if (group?.autoEnabled && clock) {
    what = `Auto lowers the ranked listings ${rate} toward the floor${clockText(noComp, clock, now)}`;
  } else if (group?.autoEnabled) {
    what = `Auto starts lowering the ranked listings ${rate} toward the floor on its next run (the first step ${NO_COMP_STEP_HOURS} h later)`;
  }
  const dry = dryRun === true ? ' The runner is in dry run: it writes nothing yet.' : '';
  return { code: 'STEP_DOWN', kind: '', text: `No comp ${where}: ${what}. Calculate writes nothing.${dry}` };
}

/**
 * The group card chip while Auto steps a compless group down, for a group
 * on Auto with its rate on and its clock running (autoNoComp; Auto off keeps
 * a stale one): "Stepping down 5%/day" when its last run stepped (summary
 * ladderStatus STEP_DOWN), the title says since when, the rate and the next
 * step; "No comp · held" when that run held the step-down (summary
 * noCompHold 'NO_MARKET': no priced market listing in the payload; shown
 * with or without a clock, as a held run leaves it as it was); "No comp ·
 * nothing to step" when that run had no comp (NO_ANCHOR_COMP) but no ranked
 * listing could step, the title says why. Nothing otherwise (a last run with
 * no ladder: an outage).
 * @param {object|null} group  a GroupView
 * @param {number} [now]       epoch ms
 * @returns {{ text: string, title: string }|null}
 */
export function noCompChip(group, now = Date.now()) {
  const pct = cleanNoCompPct(group?.criteria?.noCompDropPct);
  const clock = isObject(group?.autoNoComp) ? group.autoNoComp : null;
  if (!group?.autoEnabled || pct <= 0) return null;
  if (group.autoLastSummary?.noCompHold === NO_MARKET_HOLD) return { text: 'No comp · held', title: HELD_CHIP_TITLE };
  if (!clock) return null;
  const last = isObject(group.autoLastSummary) && Array.isArray(group.autoLastSummary.ladderStatus)
    ? group.autoLastSummary.ladderStatus : [];
  const since = easternText(clock.since);
  if (!last.includes('STEP_DOWN')) {
    if (!last.includes('NO_ANCHOR_COMP')) return null;
    return {
      text: 'No comp · nothing to step',
      title: `No comp${since ? ` since ${since}` : ''}: the ${pctText(pct)}/day step-down has no ranked listing to lower (${NO_STEPPABLE}).`,
    };
  }
  const next = easternText(clock.nextAt);
  return {
    text: `Stepping down ${pctText(pct)}/day`,
    title: [
      `No comp${since ? ` since ${since}` : ''}: Auto lowers the ranked listings ${pctText(pct)} a day toward the floor `
        + `(a step every ${NO_COMP_STEP_HOURS} h, at least $1 a step).`,
      next ? `Next step ${dueText(clock.nextAt, now)} (${next}).` : null,
    ].filter(Boolean).join('\n'),
  };
}

/**
 * The ladder bar's count of the prices an answer found: "2 step-down prices
 * (Auto's to write)" for a compless answer with the step-down on (noComp:
 * Calculate never writes them), else "2 new prices".
 * @param {number} okCount  the answer's applicable rungs (lionAutoLadder applicableRungs)
 * @param {object|null} [noComp]  the answer's noComp
 * @returns {string}
 */
export function newPricesLabel(okCount, noComp = null) {
  const s = okCount === 1 ? '' : 's';
  return isObject(noComp) ? `${okCount} step-down price${s} (Auto's to write)` : `${okCount} new price${s}`;
}

/**
 * The "Step down" chip of a rung the step-down priced (basis 'stepDown'):
 * an OK rung says by how much it was lowered (or raised to its floor, a
 * listing that sat under it); an UNCHANGED one that no step is due yet and
 * when the next one is, or (a step was due) that it sits at its floor.
 * @param {object|null} rung    a ladder answer rung
 * @param {object|null} noComp  the answer's noComp (steps, nextAt)
 * @param {number} [now]        epoch ms
 * @returns {{ text: 'Step down', title: string }|null}  null for any other rung
 */
export function stepDownFlag(rung, noComp, now = Date.now()) {
  if (rung?.basis !== STEP_BASIS) return null;
  let title = null;
  if (rung.status === 'OK') {
    const d = finite(rung.deltaB);
    if (d > 0) title = `Raised ${usd(d)} to its floor by the no-comp step-down`;
    else title = `Lowered ${d < 0 ? `${usd(d)} ` : ''}toward the floor by the no-comp step-down`;
  } else if (rung.status === 'UNCHANGED') {
    const next = easternText(noComp?.nextAt);
    if (finite(noComp?.steps) > 0) title = 'At its floor: the no-comp step-down stops there';
    else title = next ? `No step due yet; next ${dueText(noComp.nextAt, now)} (${next})` : 'No step due yet';
  }
  return title ? { text: 'Step down', title } : null;
}
